import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Autocomplete,
  Menu, ListItemIcon, ListItemText, Tooltip, TablePagination,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, Email as EmailIcon,
  Cancel as CancelIcon, MoreVert as MoreIcon, Receipt as ReceiptIcon,
  Sync as SyncIcon, Visibility as ViewIcon, OpenInNew as OpenIcon,
  Code as XmlIcon, Description as DescIcon, Share as ShareIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';

import SendDocumentDialog from '../../components/SendDocumentDialog';

const statusLabels: Record<string, string> = {
  pending: 'Pendente',
  processando_autorizacao: 'Processando',
  autorizado: 'Autorizada',
  erro_autorizacao: 'Erro',
  cancelado: 'Cancelada',
};
const statusColors: Record<string, string> = {
  pending: '#f59e0b',
  processando_autorizacao: '#3b82f6',
  autorizado: '#22c55e',
  erro_autorizacao: '#ef4444',
  cancelado: '#6b7280',
};

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const NFSe: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [nfseList, setNfseList] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuItem, setMenuItem] = useState<any>(null);

  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelJustificativa, setCancelJustificativa] = useState('');

  const [detailDialog, setDetailDialog] = useState(false);
  const [detailNfse, setDetailNfse] = useState<any>(null);

  const [emailDialog, setEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState('');

  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDocData, setSendDocData] = useState<any>(null);

  const [emitting, setEmitting] = useState(false);

  const [form, setForm] = useState<any>({
    patient_id: '', amount: '', description: '', issue_date: new Date().toISOString().split('T')[0],
    tomador_nome: '', tomador_cpf_cnpj: '', tomador_email: '', notes: '',
  });
  const [patientOptions, setPatientOptions] = useState<any[]>([]);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadNfse(); }, [search, filterStatus, page, rowsPerPage]);

  const loadData = async () => { await Promise.all([loadNfse(), loadStats()]); };

  const loadNfse = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      const res = await axios.get(getApiUrl('/clinic/nfse'), { params });
      setNfseList(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Error loading NFSe:', err);
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, page, rowsPerPage]);

  const loadStats = async () => {
    try {
      const res = await axios.get(getApiUrl('/clinic/nfse/stats'));
      setStats(res.data?.data || {});
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const searchPatients = async (q: string) => {
    if (q.length < 2) return;
    try {
      const res = await axios.get(getApiUrl('/clinic/patients'), { params: { search: q, limit: 10 } });
      setPatientOptions(res.data?.data || []);
    } catch (err) { /* ignore */ }
  };

  const handleEmit = async () => {
    if (!form.amount || !form.description) {
      setSnackbar({ open: true, message: 'Valor e descrição são obrigatórios', severity: 'error' });
      return;
    }
    try {
      setEmitting(true);
      await axios.post(getApiUrl('/clinic/nfse'), form);
      setSnackbar({ open: true, message: 'NFSe enviada para processamento!', severity: 'success' });
      setOpenDialog(false);
      resetForm();
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao emitir NFSe', severity: 'error' });
    } finally {
      setEmitting(false);
    }
  };

  const handleCheckStatus = async (id: string) => {
    try {
      const res = await axios.post(getApiUrl(`/clinic/nfse/${id}/check-status`));
      const st = res.data?.data?.status;
      setSnackbar({ open: true, message: `Status atualizado: ${statusLabels[st] || st}`, severity: 'success' });
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao consultar status', severity: 'error' });
    }
  };

  const handleCancel = async () => {
    if (!cancelJustificativa || !menuItem) return;
    try {
      await axios.post(getApiUrl(`/clinic/nfse/${menuItem.id}/cancel`), { justificativa: cancelJustificativa });
      setSnackbar({ open: true, message: 'NFSe cancelada com sucesso', severity: 'success' });
      setCancelDialog(false);
      setCancelJustificativa('');
      setMenuAnchor(null);
      setMenuItem(null);
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao cancelar', severity: 'error' });
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo || !menuItem) return;
    try {
      const emails = emailTo.split(',').map(e => e.trim()).filter(Boolean);
      await axios.post(getApiUrl(`/clinic/nfse/${menuItem.id}/email`), { emails });
      setSnackbar({ open: true, message: 'Email enviado com sucesso', severity: 'success' });
      setEmailDialog(false);
      setEmailTo('');
      setMenuAnchor(null);
      setMenuItem(null);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao enviar email', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir este registro de NFSe?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/nfse/${id}`));
      setSnackbar({ open: true, message: 'Registro excluído', severity: 'success' });
      setMenuAnchor(null);
      setMenuItem(null);
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao excluir', severity: 'error' });
    }
  };

  const handleViewDetail = async (id: string) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/nfse/${id}`));
      setDetailNfse(res.data?.data);
      setDetailDialog(true);
    } catch (err: any) {
      setSnackbar({ open: true, message: 'Erro ao carregar detalhes', severity: 'error' });
    }
    setMenuAnchor(null);
    setMenuItem(null);
  };

  const resetForm = () => {
    setForm({
      patient_id: '', amount: '', description: '', issue_date: new Date().toISOString().split('T')[0],
      tomador_nome: '', tomador_cpf_cnpj: '', tomador_email: '', notes: '',
    });
    setPatientOptions([]);
  };

  return (
    <Box sx={clinicPageContainerSx}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000', fontSize: { xs: '1.5rem', md: '2.125rem' }, mb: 0.5 }}>
            Notas Fiscais de Serviço (NFSe)
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Emissão e gestão de notas fiscais eletrônicas via FocusNFe
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={loadData} sx={{ bgcolor: 'rgba(20,184,166,0.1)', '&:hover': { bgcolor: 'rgba(20,184,166,0.2)' } }}>
              <RefreshIcon sx={{ color: '#0d9488' }} />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { resetForm(); setOpenDialog(true); }}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Emitir NFSe
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total NFSe', value: stats.total || 0, color: '#3b82f6' },
          { label: 'NFSe este mês', value: stats.total_month || 0, color: '#14b8a6' },
          { label: 'Valor mês (autorizadas)', value: formatCurrency(stats.total_amount_month || 0), color: '#22c55e' },
          { label: 'Autorizadas', value: stats.total_autorizado || 0, color: '#22c55e' },
          { label: 'Processando', value: stats.total_processando || 0, color: '#f59e0b' },
          { label: 'Com erro', value: stats.total_erro || 0, color: '#ef4444' },
        ].map((kpi, i) => (
          <Grid item xs={6} md={2} key={i}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', borderLeft: `4px solid ${kpi.color}` }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>{kpi.label}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: kpi.color, fontSize: '1.1rem' }}>{kpi.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" placeholder="Buscar por nome, número, referência..."
                value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={filterStatus} label="Status" onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="autorizado">Autorizada</MenuItem>
                  <MenuItem value="processando_autorizacao">Processando</MenuItem>
                  <MenuItem value="erro_autorizacao">Erro</MenuItem>
                  <MenuItem value="cancelado">Cancelada</MenuItem>
                  <MenuItem value="pending">Pendente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Nº NFSe</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Referência</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Tomador</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Valor</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} sx={{ color: '#14b8a6' }} /></TableCell></TableRow>
              ) : nfseList.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                  Nenhuma NFSe encontrada. Clique em "Emitir NFSe" para começar.
                </TableCell></TableRow>
              ) : nfseList.map((nf) => (
                <TableRow key={nf.id} hover sx={{ '&:hover': { bgcolor: '#f0fdfa' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                      {nf.nfse_number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                      {nf.focusnfe_ref?.substring(0, 20)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{nf.tomador_nome || nf.patient?.full_name || '-'}</Typography>
                    {nf.tomador_cpf_cnpj && (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>{nf.tomador_cpf_cnpj}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(nf.amount)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={statusLabels[nf.status] || nf.status} size="small"
                      sx={{ bgcolor: `${statusColors[nf.status] || '#94a3b8'}18`, color: statusColors[nf.status] || '#94a3b8', fontWeight: 600, fontSize: '0.7rem' }} />
                    {nf.error_message && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#ef4444', fontSize: '0.65rem', mt: 0.5, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {nf.error_message}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{nf.issue_date ? new Date(nf.issue_date).toLocaleDateString('pt-BR') : '-'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuItem(nf); }}>
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]} labelRowsPerPage="Por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`} />
      </Card>

      {/* Row actions menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => { setMenuAnchor(null); setMenuItem(null); }}>
        <MenuItem onClick={() => menuItem && handleViewDetail(menuItem.id)}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Ver detalhes</ListItemText>
        </MenuItem>
        {menuItem?.status === 'processando_autorizacao' || menuItem?.status === 'pending' ? (
          <MenuItem onClick={() => { menuItem && handleCheckStatus(menuItem.id); setMenuAnchor(null); setMenuItem(null); }}>
            <ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Consultar status</ListItemText>
          </MenuItem>
        ) : null}
        {menuItem?.url_nfse && (
          <MenuItem onClick={() => { window.open(menuItem.url_nfse, '_blank'); setMenuAnchor(null); setMenuItem(null); }}>
            <ListItemIcon><OpenIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Ver na prefeitura</ListItemText>
          </MenuItem>
        )}
        {menuItem?.status === 'autorizado' && (
          <MenuItem onClick={() => { setEmailTo(menuItem.tomador_email || ''); setEmailDialog(true); }}>
            <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Enviar por email</ListItemText>
          </MenuItem>
        )}
        {menuItem?.status === 'autorizado' && (
          <MenuItem onClick={() => { setSendDocData({ id: menuItem.id, email: menuItem.tomador_email, phone: menuItem.tomador_telefone, name: menuItem.tomador_razao_social || menuItem.tomador_nome }); setSendDialogOpen(true); setMenuAnchor(null); }}>
            <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Enviar para paciente</ListItemText>
          </MenuItem>
        )}
        {menuItem?.status === 'autorizado' && (
          <MenuItem onClick={() => setCancelDialog(true)}>
            <ListItemIcon><CancelIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
            <ListItemText sx={{ color: '#ef4444' }}>Cancelar NFSe</ListItemText>
          </MenuItem>
        )}
        {(menuItem?.status === 'erro_autorizacao' || menuItem?.status === 'pending') && (
          <MenuItem onClick={() => { menuItem && handleDelete(menuItem.id); }}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
            <ListItemText sx={{ color: '#ef4444' }}>Excluir registro</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Emit dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f0fdfa' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ color: '#14b8a6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Emitir NFSe</Typography>
          </Box>
          <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            A emissão de NFSe é assíncrona. Após o envio, a nota será processada pela prefeitura e pode levar alguns minutos para ser autorizada.
          </Alert>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Autocomplete options={patientOptions} getOptionLabel={(o: any) => `${o.full_name}${o.cpf ? ` (${o.cpf})` : ''}`}
                onInputChange={(_, v) => searchPatients(v)}
                onChange={(_, v: any) => {
                  if (v) {
                    setForm((f: any) => ({
                      ...f, patient_id: v.id, tomador_nome: v.full_name,
                      tomador_cpf_cnpj: v.cpf || '', tomador_email: v.email || '',
                    }));
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Paciente (opcional)" size="small" placeholder="Buscar paciente..." />}
                isOptionEqualToValue={(o: any, v: any) => o.id === v.id} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Nome do Tomador *" value={form.tomador_nome}
                onChange={(e) => setForm({ ...form, tomador_nome: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="CPF/CNPJ do Tomador" value={form.tomador_cpf_cnpj}
                onChange={(e) => setForm({ ...form, tomador_cpf_cnpj: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Email do Tomador" value={form.tomador_email}
                onChange={(e) => setForm({ ...form, tomador_email: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Valor (R$) *" type="number" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                inputProps={{ step: 0.01, min: 0 }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Data Emissão" type="date" value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Discriminação dos Serviços *" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                multiline rows={3} placeholder="Descreva os serviços prestados..." />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observações internas" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleEmit} disabled={emitting}
            startIcon={emitting ? <CircularProgress size={18} /> : <ReceiptIcon />}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
            {emitting ? 'Emitindo...' : 'Emitir NFSe'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f0fdfa' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescIcon sx={{ color: '#14b8a6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Detalhes da NFSe</Typography>
          </Box>
          <IconButton onClick={() => setDetailDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {detailNfse && (
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Nº NFSe</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{detailNfse.nfse_number || '-'}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Box><Chip label={statusLabels[detailNfse.status] || detailNfse.status} size="small"
                  sx={{ bgcolor: `${statusColors[detailNfse.status]}18`, color: statusColors[detailNfse.status], fontWeight: 600 }} /></Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Valor</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatCurrency(detailNfse.amount)}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Data Emissão</Typography>
                <Typography variant="body1">{detailNfse.issue_date ? new Date(detailNfse.issue_date).toLocaleDateString('pt-BR') : '-'}</Typography>
              </Grid>

              <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>Tomador</Typography></Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="textSecondary">Nome</Typography>
                <Typography variant="body2">{detailNfse.tomador_nome || '-'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="textSecondary">CPF/CNPJ</Typography>
                <Typography variant="body2">{detailNfse.tomador_cpf_cnpj || '-'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="textSecondary">Email</Typography>
                <Typography variant="body2">{detailNfse.tomador_email || '-'}</Typography>
              </Grid>

              <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>Serviço</Typography></Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Discriminação</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detailNfse.description}</Typography>
              </Grid>

              <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>Referência FocusNFe</Typography></Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#64748b' }}>{detailNfse.focusnfe_ref}</Typography>
              </Grid>

              {detailNfse.codigo_verificacao && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Código de Verificação</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{detailNfse.codigo_verificacao}</Typography>
                </Grid>
              )}

              {detailNfse.url_nfse && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">URL da Nota</Typography>
                  <Box>
                    <Button size="small" startIcon={<OpenIcon />} onClick={() => window.open(detailNfse.url_nfse, '_blank')}
                      sx={{ textTransform: 'none', color: '#14b8a6' }}>
                      Abrir na prefeitura
                    </Button>
                  </Box>
                </Grid>
              )}

              {detailNfse.error_message && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mt: 1 }}>{detailNfse.error_message}</Alert>
                </Grid>
              )}

              {detailNfse.cancellation_reason && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Cancelada em {detailNfse.cancellation_date ? new Date(detailNfse.cancellation_date).toLocaleDateString('pt-BR') : '-'}: {detailNfse.cancellation_reason}
                  </Alert>
                </Grid>
              )}

              {detailNfse.receipt && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>Recibo vinculado</Typography>
                  <Typography variant="body2">
                    {detailNfse.receipt.receipt_number} - {formatCurrency(detailNfse.receipt.amount)}
                  </Typography>
                </Grid>
              )}

              {detailNfse.notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Observações</Typography>
                  <Typography variant="body2">{detailNfse.notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {detailNfse?.status === 'processando_autorizacao' && (
            <Button startIcon={<SyncIcon />} onClick={() => { handleCheckStatus(detailNfse.id); setDetailDialog(false); }}
              sx={{ textTransform: 'none', color: '#3b82f6' }}>
              Consultar status
            </Button>
          )}
          <Button onClick={() => setDetailDialog(false)} sx={{ textTransform: 'none' }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#ef4444' }}>Cancelar NFSe</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
            Informe a justificativa para o cancelamento. Esta ação não pode ser desfeita.
          </Typography>
          <TextField fullWidth label="Justificativa *" value={cancelJustificativa}
            onChange={(e) => setCancelJustificativa(e.target.value)}
            multiline rows={3} placeholder="Motivo do cancelamento da NFSe..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCancelDialog(false); setCancelJustificativa(''); }} sx={{ textTransform: 'none' }}>Voltar</Button>
          <Button variant="contained" onClick={handleCancel} disabled={!cancelJustificativa}
            sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, textTransform: 'none' }}>
            Confirmar Cancelamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email dialog */}
      <Dialog open={emailDialog} onClose={() => setEmailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar NFSe por Email</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Email(s)" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
            placeholder="email@exemplo.com (separe múltiplos com vírgula)" sx={{ mt: 1 }}
            helperText="Separe múltiplos emails com vírgula" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialog(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSendEmail} disabled={!emailTo}
            startIcon={<EmailIcon />}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none' }}>
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      <SendDocumentDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        documentType="nfse"
        documentId={sendDocData?.id || ''}
        patientEmail={sendDocData?.email}
        patientPhone={sendDocData?.phone}
        patientName={sendDocData?.name}
        documentLabel="Nota Fiscal"
      />

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NFSe;
